import React from 'react';
import PropTypes from 'prop-types';
import ModalCurtain from '@thumbtack/tp-ui-react-modal-curtain';
import classNames from 'classnames';
import Transition from './transition.jsx';
import styles from './animated-wrapper.module.scss';

// These values are duplicated in the Sass.
const TRANSITION_OPEN_SPEED = 300;
const TRANSITION_CLOSE_SPEED = 250;

const propTypes = {
    /**
     * Content that appears within the modal.
     */
    children: PropTypes.node,
    /**
     * Function that fires to close the modal.
     */
    onCloseClick: PropTypes.func.isRequired,
    /**
     * Function that fires once the modal has opened and transitions have ended.
     */
    onOpenFinish: PropTypes.func,
    /**
     * Function that fires once the modal has closed and transitions have ended.
     */
    onCloseFinish: PropTypes.func,
    /**
     * Determines if the modal should close when clicking on the curtain, outside of the `children`.
     */
    shouldCloseOnCurtainClick: PropTypes.bool,
    /**
     * Should the modal appear open.
     */
    isOpen: PropTypes.bool,
    /**
     * Sets the max-width of the modal container.
     */
    width: PropTypes.oneOf(['narrow', 'medium', 'wide']),
};

const defaultProps = {
    children: undefined,
    onOpenFinish: undefined,
    onCloseFinish: undefined,
    isOpen: false,
    shouldCloseOnCurtainClick: true,
    width: 'medium',
};

/**
 * `ModalDefaultAnimatedWrapper` is an exported component that we export for developers that want access to
 * `ModalDefault` without padding and a close button. We export it as a named export instead of
 * creating a `hasNoPadding` prop partly to discourage the use of `ModalDefault` without padding.
 *
 * This component uses `ModalCurtain` and includes the backdrop, transition, and white modal
 * wrapper that is available at a few widths.
 */
const ModalDefaultAnimatedWrapper = ({
    children,
    isOpen,
    onCloseClick,
    onCloseFinish,
    onOpenFinish,
    shouldCloseOnCurtainClick,
    width,
}) => (
    <Transition
        in={isOpen}
        timeout={{
            enter: TRANSITION_OPEN_SPEED,
            exit: TRANSITION_CLOSE_SPEED,
        }}
        onEntered={onOpenFinish}
        onExited={onCloseFinish}
    >
        {transitionStage => (
            <ModalCurtain stage={transitionStage} onCloseClick={onCloseClick}>
                {({ curtainClassName, curtainOnClick }) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                        className={classNames({
                            [curtainClassName]: true,
                            [styles.curtain]: true,
                            [styles.curtainOpen]: isOpen,
                        })}
                        onClick={shouldCloseOnCurtainClick ? curtainOnClick : undefined}
                        data-test="thumbprint-modal-curtain"
                    >
                        <div
                            className={classNames({
                                [styles.wrapper]: true,
                                [styles.wrapperOpen]: isOpen,
                                [styles.wrapperWide]: width === 'wide',
                                [styles.wrapperNarrow]: width === 'narrow',
                                [styles.wrapperMedium]: width === 'medium',
                            })}
                        >
                            <div
                                className={classNames({
                                    [styles.container]: true,
                                })}
                            >
                                {children}
                            </div>
                        </div>
                    </div>
                )}
            </ModalCurtain>
        )}
    </Transition>
);

ModalDefaultAnimatedWrapper.propTypes = propTypes;
ModalDefaultAnimatedWrapper.defaultProps = defaultProps;

export default ModalDefaultAnimatedWrapper;
